import React, { useState } from 'react';

import { Checkbox, FormControlLabel, FormHelperText } from '@material-ui/core';
import SearchFilterButton from '../../../common/SearchFilterButton';
import SearchFilterDrawer, {
  BaseSearchFilterProps
} from '../../../common/SearchFilterDrawer';
import InputDatePicker from '../../../common/forms/InputDatePicker';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import { User } from '../../../user/User.interface';
import { TaskType } from '../../task-types/TaskType.interface';
import MultiUserSelector from '../../users/components/MultiUserSelector';
import DocumentStatusFilter from './DocumentStatusFilter';
import DocumentTypeFilter from './DocumentTypeFilter';

export interface DocumentFilterOptions {
  assigneeId: number[];
  type: string[];
  status: string[];
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
  assignedToMe?: boolean;
}

function createInitialOptions(
  options?: Partial<DocumentFilterOptions>
): DocumentFilterOptions {
  return { assigneeId: [], type: [], status: [], ...options };
}

export interface DocumentBoardFilterProps
  extends BaseSearchFilterProps<DocumentFilterOptions> {
  options?: Partial<DocumentFilterOptions>;
}

const DocumentBoardFilter: React.FC<DocumentBoardFilterProps> = (
  props: DocumentBoardFilterProps
) => {
  const { onFilter, options: initialOptions } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<DocumentFilterOptions>(() => {
    return createInitialOptions(initialOptions);
  });

  const updateOptions = (updatedData: Partial<DocumentFilterOptions>) => {
    setOptions({ ...options, ...updatedData });
  };

  return (
    <>
      <SearchFilterButton onClick={() => setOpen(true)} />
      <SearchFilterDrawer
        open={open}
        title='Filter Documents'
        onClose={() => setOpen(false)}
        onClear={() => setOptions(createInitialOptions())}
        onFilter={() => {
          setOpen(false);
          onFilter?.(options);
        }}
      >
        <InlineFormControl label='Assignee'>
          <MultiUserSelector
            value={options.assigneeId}
            disabled={options.assignedToMe}
            textFieldProps={{ label: undefined, placeholder: 'Select Users' }}
            onChange={(users: User[]) => {
              updateOptions({
                assigneeId: users.map((user: User) => user.id)
              });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Document Type'>
          <DocumentTypeFilter
            value={options.type}
            onChange={(taskTypes: TaskType[] | null) => {
              updateOptions({
                type: taskTypes?.map((tt: TaskType) => tt.name)
              });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Document Status'>
          <DocumentStatusFilter
            value={options.status}
            onChange={status => updateOptions({ status })}
          />
        </InlineFormControl>
        <InlineFormControl label='Due Date From'>
          <InputDatePicker
            clearable
            autoCloseOnSelect
            placeholder='Select or Enter Date'
            value={options.dueDateFrom}
            onChange={value => {
              const date = value ? value.toISOString().split('T')[0] : value;
              updateOptions({ dueDateFrom: date });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Due Date To'>
          <InputDatePicker
            clearable
            autoCloseOnSelect
            placeholder='Select or Enter Date'
            value={options.dueDateTo}
            pickerProps={{ minDate: options.dueDateFrom }}
            onChange={value => {
              const date = value ? value.toISOString().split('T')[0] : value;
              updateOptions({ dueDateTo: date });
            }}
          />
        </InlineFormControl>
        <InlineFormControl>
          <FormControlLabel
            label='Assigned To Me'
            control={
              <Checkbox
                color='primary'
                checked={options.assignedToMe || false}
                onChange={(_, checked) => {
                  const update: Partial<DocumentFilterOptions> = {
                    assignedToMe: !!checked
                  };
                  if (checked) {
                    update.assigneeId = [];
                  }
                  updateOptions(update);
                }}
              />
            }
          />
          <FormHelperText>
            Check this to show assigned items to the current user only.
          </FormHelperText>
        </InlineFormControl>
      </SearchFilterDrawer>
    </>
  );
};

export default DocumentBoardFilter;
